import React, { useState, useEffect } from 'react';

interface PhotoPackagePayment {
  _id: string;
  originalOrder: {
    _id: string;
    treesOrdered: number;
    customerName: string;
  };
  customer: {
    _id: string;
    name: string;
    email: string;
  };
  packageType: 'first-year' | 'three-year' | 'lifetime';
  packageName?: string;
  totalPhotoCount: number;
  amount: number;
  currency: string;
  paymentMethod: string;
  manualPayment?: {
    evidenceFiles: string[];
    verificationStatus: string;
  };
  createdAt: string;
}

interface PhotoPackagePaymentsTabProps {
  onVerifyPayment: (paymentId: string, notes?: string) => void;
  onRejectPayment: (paymentId: string, reason: string) => void;
  onViewEvidence: (payment: PhotoPackagePayment) => void;
}

export const PhotoPackagePaymentsTab: React.FC<PhotoPackagePaymentsTabProps> = ({
  onVerifyPayment,
  onRejectPayment,
  onViewEvidence
}) => {
  const [pendingPayments, setPendingPayments] = useState<PhotoPackagePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<PhotoPackagePayment | null>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadPendingPayments();
  }, []);

  const loadPendingPayments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/photo-package-payments/pending', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingPayments(data.pendingPayments);
      }
    } catch (error) {
      console.error('Load pending photo package payments error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPackageName = (type: string) => {
    const names = {
      'first-year': 'First Year Journey',
      'three-year': 'Three Year Story',
      'lifetime': 'Lifetime Growth'
    };
    return names[type as keyof typeof names] || type;
  };

  const handleVerifyClick = (payment: PhotoPackagePayment) => {
    setSelectedPayment(payment);
    setVerificationNotes('');
    setShowVerifyModal(true);
  };

  const handleRejectClick = (payment: PhotoPackagePayment) => {
    setSelectedPayment(payment);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleConfirmVerify = async () => {
    if (!selectedPayment) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/photo-package-payments/${selectedPayment._id}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ verificationNotes })
      });

      if (response.ok) {
        await loadPendingPayments(); // Reload the list
        setShowVerifyModal(false);
        setSelectedPayment(null);
        // Could also call onVerifyPayment callback if needed
      }
    } catch (error) {
      console.error('Verify payment error:', error);
    }
  };

  const handleConfirmReject = async () => {
    if (!selectedPayment || !rejectionReason.trim()) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/photo-package-payments/${selectedPayment._id}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rejectionReason: rejectionReason.trim() })
      });

      if (response.ok) {
        await loadPendingPayments(); // Reload the list
        setShowRejectModal(false);
        setSelectedPayment(null);
        // Could also call onRejectPayment callback if needed
      }
    } catch (error) {
      console.error('Reject payment error:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        color: '#666'
      }}>
        Loading photo package payments...
      </div>
    );
  }

  return (
    <>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333', margin: 0 }}>
            📸 Pending Photo Package Payments
          </h2>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            {pendingPayments.length} payments pending
          </span>
        </div>

        {pendingPayments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            No pending photo package payments
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Customer</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Package</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Amount</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Order</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Date</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#333' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayments.map((payment) => (
                  <tr key={payment._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: '600' }}>{payment.customer.name}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>{payment.customer.email}</div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: '600' }}>{getPackageName(payment.packageType)}</div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{payment.totalPhotoCount} photos</div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600' }}>${(payment.amount / 100).toFixed(2)}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>🇨🇴 Bancolombia</div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontSize: '0.9rem' }}>#{payment.originalOrder._id.slice(-6)}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        Tree order
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontSize: '0.9rem' }}>
                        {new Date(payment.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => onViewEvidence(payment)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          View Evidence
                        </button>
                        <button
                          onClick={() => handleVerifyClick(payment)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => handleRejectClick(payment)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Verify Modal */}
      {showVerifyModal && selectedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ color: '#228B22', marginBottom: '1rem' }}>
              Verify Photo Package Payment
            </h3>
            <div style={{ marginBottom: '1.5rem', color: '#666' }}>
              <p><strong>Customer:</strong> {selectedPayment.customer.name}</p>
              <p><strong>Package:</strong> {getPackageName(selectedPayment.packageType)}</p>
              <p><strong>Amount:</strong> ${(selectedPayment.amount / 100).toFixed(2)}</p>
              <p><strong>Order:</strong> #{selectedPayment.originalOrder._id.slice(-6)}</p>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Verification Notes (Optional):
              </label>
              <textarea
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder="Add any notes about the verification..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowVerifyModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmVerify}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                ✓ Verify Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ color: '#dc3545', marginBottom: '1rem' }}>
              Reject Photo Package Payment
            </h3>
            <div style={{ marginBottom: '1.5rem', color: '#666' }}>
              <p><strong>Customer:</strong> {selectedPayment.customer.name}</p>
              <p><strong>Package:</strong> {getPackageName(selectedPayment.packageType)}</p>
              <p><strong>Amount:</strong> ${(selectedPayment.amount / 100).toFixed(2)}</p>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#dc3545' }}>
                Rejection Reason (Required):
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a clear reason for rejecting this payment..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.75rem',
                  border: '2px solid #dc3545',
                  borderRadius: '6px',
                  resize: 'vertical'
                }}
                required
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReject}
                disabled={!rejectionReason.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: rejectionReason.trim() ? '#dc3545' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: rejectionReason.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: '600'
                }}
              >
                ✗ Reject Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};